import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiClock, FiMap, FiUsers, FiFileText, FiAward } from 'react-icons/fi';
import StoreButtons from '../components/StoreButtons';
import Reel from '../components/Reel';
import { getProduct } from '../data/catalog';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-blue/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-blue/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-blue" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const CaseUnknown = () => {
  const PRODUCT = getProduct('caseunknown');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-blue/10 border border-accent-blue/20 text-accent-blue font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={FiSearch} /> Detective Mystery — Coming Soon
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic">
              Case<span className="text-accent-blue">Unknown</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "Every case has a truth. Read the file, break the suspects, and name who did it — before the clock runs out."
            </p>
            <div className="flex flex-col items-center justify-center gap-6">
              <StoreButtons stores={PRODUCT.stores} accentColor="bg-accent-blue hover:bg-accent-blue/90" className="justify-center" />
              <span className="text-xs text-muted font-mono uppercase tracking-[0.16em]">
                Coming soon to PC and Mac · {PRODUCT.year}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Description */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">{PRODUCT.desc}</p>
        </div>
      </section>

      {/* Reel — real screenshots from the first case, The Gilded Frame */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">First case: The Gilded Frame</p>
          <Reel frames={PRODUCT.frames} label="CaseUnknown" sound={false} ratio="aspect-[16/10]" />
          <p className="mt-4 text-center text-xs text-muted font-mono uppercase tracking-[0.16em]">
            In-game footage · A painting vanishes from a sealed museum. Read the room, break the alibis, name the thief.
          </p>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">The Investigation</h2>
            <p className="text-muted">A detective game about reading people, weighing evidence, and making the call.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={FiFileText} title="Real Case Files" desc="Every case is a story: motives, alibis, contradictions. Read carefully — the truth is in the details." />
            <FeatureCard icon={FiUsers} title="Interrogate Suspects" desc="Question each suspect and read their answers. Press the right thread and the story cracks open." />
            <FeatureCard icon={FiClock} title="Beat the Clock" desc="Interrogations are timed. You get one shot at each suspect, so ask what matters." />
            <FeatureCard icon={FiMap} title="Case Map" desc="Move through the case at your own pace, following leads across the board." />
            <FeatureCard icon={FiAward} title="Career Mode" desc="Start with easy cases and earn your way up to the hard ones as your reputation grows." />
            <FeatureCard icon={FiSearch} title="Name the Culprit" desc="Weigh everything you have gathered and make the accusation. Get it right, close the case." />
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="rule-label mb-6">Where to play</p>
          <h2 className="text-3xl font-black text-white mb-6 uppercase">Launching across every store</h2>
          <p className="text-muted mb-10">
            CaseUnknown is coming to the stores below. Follow MediaVerse Labs to know the moment it goes live.
          </p>
          <StoreButtons stores={PRODUCT.stores} accentColor="bg-accent-blue hover:bg-accent-blue/90" className="justify-center" />
        </div>
      </section>

      {/* Final */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-blue/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={FiSearch} className="w-16 h-16 text-accent-blue mx-auto mb-8 animate-pulse" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">Can you crack the case?</h2>
          <p className="text-muted mb-10 text-lg">
            A story-driven detective game. No microtransactions. No online requirement. Just you and the truth.
          </p>
          <StoreButtons stores={PRODUCT.stores} accentColor="bg-accent-blue hover:bg-accent-blue/90" className="justify-center" />
        </div>
      </section>
    </div>
  );
};

export default CaseUnknown;
